import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Stack,
  SvgIcon,
  Typography,
  CircularProgress,
  TextField,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { ReactComponent as CloseModal } from 'assets/images/ic_close_modal.svg';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';

interface ExternalLinkDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSave?: (data: { title: string; url: string }) => void;
}

const ExternalLinkDialog: React.FC<ExternalLinkDialogProps> = ({
  open,
  title,
  onClose,
  onSave,
}) => {
  const [linkTitle, setLinkTitle] = useState('');
  const [url, setUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const isValidUrl = (value: string): boolean => {
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isFormValid = useMemo(
    () =>
      linkTitle.trim() !== '' && url.trim() !== '' && isValidUrl(url.trim()),
    [linkTitle, url]
  );

  const handleSave = (): void => {
    if (!isFormValid) return;

    setIsSaving(true);

    setTimeout(() => {
      onSave?.({ title: linkTitle, url });
      setIsSaving(false);
      onClose();
      // Reset form
      setLinkTitle('');
      setUrl('');
    }, 1000);
  };

  const handleCancel = (): void => {
    setLinkTitle('');
    setUrl('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={4}
        >
          <Box pr={2}>
            <Typography variant="h5" fontWeight={700} mb={1}>
              {title}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Link to external documentation or evidence stored elsewhere.
            </Typography>
          </Box>

          <SvgIcon
            component={CloseModal}
            sx={{ cursor: 'pointer', fontSize: 20 }}
            onClick={handleCancel}
          />
        </Stack>

        {/* Form */}
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={1}
              fontWeight={500}
            >
              Title
            </Typography>

            <TextField
              placeholder="e.g. SharePoint Document"
              fullWidth
              size="small"
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
              disabled={isSaving}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={1}
              fontWeight={500}
            >
              URL
            </Typography>

            <TextField
              placeholder="https://..."
              fullWidth
              size="small"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isSaving}
              error={url.trim() !== '' && !isValidUrl(url.trim())}
              helperText={
                url.trim() !== '' && !isValidUrl(url.trim())
                  ? 'Please enter a valid URL (e.g. https://example.com)'
                  : ''
              }
            />
          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
            <AppPrimaryButtonOutlined
              title="Cancel"
              className="btn-small"
              onClick={handleCancel}
              disabled={isSaving}
            />
            <AppPrimaryButton
              className="btn-small"
              type="button"
              startIcon={
                isSaving ? <CircularProgress size={20} /> : <SaveIcon />
              }
              onClick={handleSave}
              title={isSaving ? 'Saving...' : 'Add link'}
              disabled={!isFormValid || isSaving}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalLinkDialog;
